import { ApiService, type PaginatedResponse } from '@sparklayer-io/spark-dash-core/types/api';
import type { PaginationOptions } from '@sparklayer-io/spark-dash-ui/utils/pagination';
import type { CsvDateOrder } from '../csv/date-order';
import type {
  CompanyImportResult,
  CompanyListQuery,
  CompanyLookup,
  CompanyRequest,
  SectionCustomerImportResult,
} from '../types/api';
import type { Company, CompanyDetail } from '../types/company';
import { AddressToolsApi, type AddressToolsApiContract } from './address-tools.api';
import { CompanyApi, type CompanyApiContract } from './company.api';

export interface CompaniesApiContract {
  list(query: CompanyListQuery, pagination: PaginationOptions): Promise<PaginatedResponse<Company>>;
  create(payload: CompanyRequest): Promise<CompanyDetail>;
  /** Create or update companies and sections from a CSV file, reporting the outcome per row. */
  importCsv(csv: string, dateOrder?: CsvDateOrder): Promise<CompanyImportResult>;
  /** Add customers to sections from a CSV file, reporting the outcome per row. */
  importSectionCustomersCsv(csv: string): Promise<SectionCustomerImportResult>;
  company(lookup: CompanyLookup): CompanyApiContract;
  addressTools(): AddressToolsApiContract;
}

declare module '@sparklayer-io/spark-dash-core/context/page-context' {
  interface Apis {
    companies: CompaniesApiContract;
  }
}

/**
 * A service to interact with companies via the SparkLayer API.
 */
export class CompaniesApi extends ApiService implements CompaniesApiContract {
  public list(
    query: CompanyListQuery,
    pagination: PaginationOptions,
  ): Promise<PaginatedResponse<Company>> {
    const params = pagination.toQueryParams();
    if (query.search_term) {
      params.set('search_term', query.search_term);
    }

    return this.api.proxy({
      method: 'GET',
      path: `/v2/companies?${params}`,
    });
  }

  public create(payload: CompanyRequest): Promise<CompanyDetail> {
    return this.api.proxy({
      method: 'POST',
      path: '/v2/companies',
      payload,
    });
  }

  public importCsv(csv: string, dateOrder?: CsvDateOrder): Promise<CompanyImportResult> {
    const query = dateOrder ? `?${new URLSearchParams({ date_order: dateOrder })}` : '';
    return this.api.proxy({
      method: 'POST',
      path: `/v2/companies/import${query}`,
      payload: csv,
      contentType: 'text/csv',
    });
  }

  public importSectionCustomersCsv(csv: string): Promise<SectionCustomerImportResult> {
    return this.api.proxy({
      method: 'POST',
      path: '/v2/companies/import-section-customers',
      payload: csv,
      contentType: 'text/csv',
    });
  }

  public company(lookup: CompanyLookup): CompanyApiContract {
    return new CompanyApi(this.api, lookup);
  }

  public addressTools(): AddressToolsApiContract {
    return new AddressToolsApi(this.api);
  }
}
